import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Select, Switch, Input, Button, Tag, message, Modal, DatePicker, Row, Col } from "antd";
import SectionBlock from "./components/section-block";
import { SECTION_IDS } from "./constants";
import { CASE_CATEGORY, DEVIATION_CATEGORY_OPTIONS, TNCCredit } from "constants/enums";
import CaseService from "services/case-service";
import BundledLimitAssignment from "../bundled-limit-assignment";

/**
 * Actionable — the credit-team flags feeding the final sanction. Mirrors the
 * block on Credit Appraisal so the credit checker view has the same controls.
 * Writes go through the same CaseService endpoints case-details-summary uses,
 * then re-fetch caseDetail / caseDetailFields via the props callbacks so all
 * downstream consumers see the update.
 *
 * Interactive fields:
 *  - Case Category           → CaseService.updateCaseCategoryTag
 *  - Deviation Tag / Category → CaseService.updateCaseDetailFields
 *  - Is High Value Case      → CaseService.updateCaseDetail
 *  - Credit T&C              → CaseService.updateCaseDetail (json_data.tncCredit.creditRemark)
 *
 *  - Growth Plan             → in-place modal (same flow as case-details-summary),
 *                              persisted via CaseService.updateCaseDetailV2.
 *
 * Residence CPV still opens the modal owned by the Credit Appraisal tab; that
 * button deep-links there so the flow isn't duplicated.
 */

const { Option } = Select;
const { TextArea } = Input;

// Same "Yes / No" map Credit Appraisal uses for the Deviation Tag select.
const DEVIATION_TAG_OPTIONS = [
    { label: "Yes", value: true },
    { label: "No", value: false }
];

const readField = (obj, key) => {
    if (!obj) return undefined;
    if (obj.get) return obj.get(key);
    return obj[key];
};

const labelForValue = (mapObj, value) => {
    if (value === null || value === undefined || value === "") return null;
    try {
        const entry = mapObj.findEntry(v => v === value);
        return entry ? entry[0] : null;
    } catch (e) {
        return null;
    }
};

class ActionableSection extends React.Component {
    // Draft state for the free-text inputs so keystrokes don't hit the API on
    // every character; on blur we save.
    state = {
        creditRemarkDraft: null,
        highValueSaving: false,
        caseCategorySaving: false,
        deviationSaving: false,
        creditRemarkSaving: false,
        growthPlanModalVisible: false,
        growthPlanSaving: false,
        growthPlan: { aum: "", noOfRTs: "", timeline: null },
        // Deviation Tag "Yes" flow — a modal captures the (mandatory) category
        // before the flag is committed, matching case-details-summary.
        deviationTagModalVisible: false,
        pendingDeviationCategory: null,
        deviationTagSubmitting: false,
        // Terms & Condition popup — Reason + Credit T&C + Post-Sanction remark.
        tncModalVisible: false,
        tncSaving: false,
        tncCredit: { deviationTag: null, creditRemark: "", postSanctionRemark: "" }
    };

    // json_data off caseData, supporting both Immutable Map and plain object.
    getJsonData() {
        return (
            (this.props.caseData &&
                (this.props.caseData.get
                    ? this.props.caseData.get("json_data")
                    : this.props.caseData.json_data)) ||
            {}
        );
    }

    getCreditRemark() {
        if (this.state.creditRemarkDraft !== null) {
            return this.state.creditRemarkDraft;
        }
        const jsonData = this.getJsonData();
        return (jsonData.tncCredit && jsonData.tncCredit.creditRemark) || "";
    }

    onChangeCaseCategory = value => {
        this.setState({ caseCategorySaving: true });
        CaseService.updateCaseCategoryTag(this.props.caseId, {
            case_category: value
        })
            .then(() => {
                if (this.props.fetchCaseDetail) {
                    this.props.fetchCaseDetail(this.props.caseId);
                }
                if (this.props.fetchCaseDetailFields) {
                    this.props.fetchCaseDetailFields(this.props.caseId);
                }
                message.success("Case category updated");
            })
            .catch(() => message.error("Failed to update case category"))
            .finally(() => this.setState({ caseCategorySaving: false }));
    };

    onChangeDeviationTag = value => {
        // Tagging "Yes" opens a modal that forces a Deviation Category before
        // committing (same as case-details-summary). "No" clears the flag.
        if (value === true) {
            this.setState({
                deviationTagModalVisible: true,
                pendingDeviationCategory:
                    readField(this.props.caseDetailFields, "deviation_category") ||
                    null
            });
            return;
        }
        this.setState({ deviationSaving: true });
        CaseService.updateCaseDetailFields(this.props.caseId, {
            is_deviation_tagged: false
        })
            .then(() => {
                if (this.props.fetchCaseDetailFields) {
                    this.props.fetchCaseDetailFields(this.props.caseId);
                }
                message.success("Deviation tag updated");
            })
            .catch(() => message.error("Failed to update deviation tag"))
            .finally(() => this.setState({ deviationSaving: false }));
    };

    submitDeviationTagModal = () => {
        const category = this.state.pendingDeviationCategory;
        if (!category) {
            message.error("Please select a Deviation Category.");
            return;
        }
        this.setState({ deviationTagSubmitting: true });
        CaseService.updateCaseDetailFields(this.props.caseId, {
            is_deviation_tagged: true,
            deviation_category: category
        })
            .then(() => {
                if (this.props.fetchCaseDetailFields) {
                    this.props.fetchCaseDetailFields(this.props.caseId);
                }
                message.success("Deviation tag updated");
                this.setState({
                    deviationTagModalVisible: false,
                    pendingDeviationCategory: null
                });
            })
            .catch(() => message.error("Failed to update deviation tag"))
            .finally(() => this.setState({ deviationTagSubmitting: false }));
    };

    onChangeDeviationCategory = value => {
        this.setState({ deviationSaving: true });
        CaseService.updateCaseDetailFields(this.props.caseId, {
            deviation_category: value
        })
            .then(() => {
                if (this.props.fetchCaseDetailFields) {
                    this.props.fetchCaseDetailFields(this.props.caseId);
                }
                message.success("Deviation category updated");
            })
            .catch(() => message.error("Failed to update deviation category"))
            .finally(() => this.setState({ deviationSaving: false }));
    };

    onChangeHighValueCase = value => {
        this.setState({ highValueSaving: true });
        CaseService.updateCaseDetail(this.props.caseId, {
            is_high_value_case: value
        })
            .then(() => {
                if (this.props.fetchCaseDetail) {
                    this.props.fetchCaseDetail(this.props.caseId);
                }
                message.success("High-value flag updated");
            })
            .catch(() => message.error("Failed to update high-value flag"))
            .finally(() => this.setState({ highValueSaving: false }));
    };

    saveTncField = (fieldKey, savingKey, value) => {
        const jsonData =
            (this.props.caseData && (this.props.caseData.get
                ? this.props.caseData.get("json_data")
                : this.props.caseData.json_data)) || {};
        const tncCredit = { ...(jsonData.tncCredit || {}), [fieldKey]: value };
        const nextJson = { ...jsonData, tncCredit };
        this.setState({ [savingKey]: true });
        CaseService.updateCaseDetail(this.props.caseId, {
            json_data: nextJson
        })
            .then(() => {
                if (this.props.fetchCaseDetail) {
                    this.props.fetchCaseDetail(this.props.caseId);
                }
                message.success("Saved");
            })
            .catch(() => message.error("Save failed"))
            .finally(() => this.setState({ [savingKey]: false }));
    };

    onBlurCreditRemark = () => {
        const draft = this.state.creditRemarkDraft;
        if (draft === null) return;
        this.saveTncField("creditRemark", "creditRemarkSaving", draft);
        this.setState({ creditRemarkDraft: null });
    };

    // Growth Plan — same modal/flow the Credit Appraisal (case-details-summary)
    // Actionable block uses: edit AUM / RT count / timeline, persist under
    // json_data.growth_plan_details plus a generated growth_plan_parameter
    // sentence via updateCaseDetailV2, then re-fetch caseDetail.
    generateGrowthPlanSentence = ({ aum, noOfRTs, timeline }) => {
        if (!aum && !noOfRTs && !timeline) return "";
        const formatted = timeline ? moment(timeline).format("DD MMM YYYY") : "";
        return `Planned AUM of ₹${aum}, with ${noOfRTs} RTs, targeted by ${formatted}.`;
    };

    openGrowthPlanModal = () => {
        const gp = this.getJsonData().growth_plan_details || {};
        this.setState({
            growthPlanModalVisible: true,
            growthPlan: {
                aum: gp.aum || "",
                noOfRTs: gp.noOfRTs || "",
                timeline:
                    gp.timeline && moment(gp.timeline).isValid()
                        ? moment(gp.timeline)
                        : null
            }
        });
    };

    handleGrowthPlanChange = (key, value) => {
        this.setState({
            growthPlan: { ...this.state.growthPlan, [key]: value }
        });
    };

    onGrowthPlanSubmit = () => {
        const { growthPlan } = this.state;
        const formattedTimeline = growthPlan.timeline
            ? growthPlan.timeline.format("YYYY-MM-DD")
            : null;
        const growthPlanSentence = this.generateGrowthPlanSentence({
            ...growthPlan,
            timeline: formattedTimeline
        });
        const existingJson = this.getJsonData();
        const payload = {
            growth_plan_parameter: growthPlanSentence,
            json_data: {
                ...existingJson,
                growth_plan_details: {
                    aum: growthPlan.aum,
                    noOfRTs: growthPlan.noOfRTs,
                    timeline: formattedTimeline
                }
            }
        };
        this.setState({ growthPlanSaving: true });
        const loadingMessage = message.loading("Updating...", 0);
        CaseService.updateCaseDetailV2(this.props.caseId, payload)
            .then(() => {
                loadingMessage();
                message.success("Growth plan submitted successfully");
                if (this.props.fetchCaseDetail) {
                    this.props.fetchCaseDetail(this.props.caseId);
                }
                this.setState({ growthPlanModalVisible: false });
            })
            .catch(() => {
                loadingMessage();
                message.error("Failed to submit");
            })
            .finally(() => this.setState({ growthPlanSaving: false }));
    };

    // Bundled Limit Assignment — copied verbatim from the Credit Appraisal tab
    // (updateChildCaseLimit): validate the two percentages, PATCH via
    // CaseService.updateBundledLimit, then optimistically update caseDetail so
    // the inputs reflect the saved values.
    updateChildCaseLimit = ({
        bundled_limit_percentage,
        max_tl_limit_utilisation_perc
    }) => {
        const childMovedToLms =
            this.props.caseDetailFields &&
            this.props.caseDetailFields.get("child_moved_to_lms") === true;
        if (
            !childMovedToLms &&
            (bundled_limit_percentage <= 0 || bundled_limit_percentage > 100)
        ) {
            message.error("Please enter Child Case Limit between 1 and 100");
            return;
        }
        if (
            max_tl_limit_utilisation_perc <= 0 ||
            max_tl_limit_utilisation_perc > 100
        ) {
            message.error(
                "Please enter Max TL Limit Utilisation between 1 and 100"
            );
            return;
        }
        const loadingMessage = message.loading("Updating...", 0);
        CaseService.updateBundledLimit(this.props.caseId, {
            bundled_limit_percentage,
            max_tl_limit_utilisation_perc
        })
            .then(() => {
                loadingMessage();
                if (this.props.updateCaseDetail && this.props.caseDetail) {
                    this.props.updateCaseDetail(
                        this.props.caseId,
                        this.props.caseDetail
                            .set(
                                "bundled_limit_percentage",
                                bundled_limit_percentage
                            )
                            .set(
                                "max_tl_limit_utilisation_perc",
                                max_tl_limit_utilisation_perc
                            )
                    );
                }
            })
            .catch(() => {
                loadingMessage();
                message.error(
                    "Failed to update bundled limits. Please try again."
                );
            });
    };

    // Terms & Condition popup — Reason (deviationTag) + Credit T&C +
    // Post-Sanction remark, persisted under json_data.tncCredit. Same flow as
    // case-details-summary's openTNCCreditModal / onTNCCreditSubmit.
    openTNCCreditModal = () => {
        const tnc = this.getJsonData().tncCredit || {};
        this.setState({
            tncModalVisible: true,
            tncCredit: {
                deviationTag: tnc.deviationTag || null,
                creditRemark: tnc.creditRemark || "",
                postSanctionRemark: tnc.postSanctionRemark || ""
            }
        });
    };

    handleTncChange = (key, value) => {
        this.setState(prev => ({
            tncCredit: { ...prev.tncCredit, [key]: value }
        }));
    };

    onTNCCreditSubmit = () => {
        const { tncCredit } = this.state;
        const existingJson = this.getJsonData();
        const payload = {
            json_data: {
                ...existingJson,
                tncCredit: {
                    deviationTag: tncCredit.deviationTag || null,
                    creditRemark: tncCredit.creditRemark || "",
                    postSanctionRemark: tncCredit.postSanctionRemark || ""
                }
            }
        };
        this.setState({ tncSaving: true });
        const loadingMessage = message.loading("Updating...", 0);
        CaseService.updateCaseDetailV2(this.props.caseId, payload)
            .then(() => {
                loadingMessage();
                message.success("T&C Credit updated successfully");
                if (this.props.fetchCaseDetail) {
                    this.props.fetchCaseDetail(this.props.caseId);
                }
                this.setState({ tncModalVisible: false });
            })
            .catch(() => {
                loadingMessage();
                message.error("Failed to submit T&C Credit");
            })
            .finally(() => this.setState({ tncSaving: false }));
    };

    // Case Category is only re-assignable on Credit Appraisal (stage 7), and
    // only by a Credit Manager (group 7) — same gate the Case Details tab
    // uses for the same field.
    canEditCaseCategory = () =>
        Number(this.props.stageId) === 7 &&
        Boolean(
            this.props.userGroup &&
                this.props.userGroup.some(g => g && g.id === 7)
        );

    // Deviation Tag/Category and the Credit T&C remark are only actionable
    // at the credit stages — same gate used elsewhere in Credit Checker.
    canEditDeviation = () => [7, 8].includes(Number(this.props.stageId));

    canEditCreditRemark = () => [7, 8].includes(Number(this.props.stageId));

    // Growth Plan and "Is High Value Case" are only actionable at the
    // credit stages — same gate used elsewhere in Credit Checker.
    canEditGrowthPlan = () => [7, 8].includes(Number(this.props.stageId));

    canEditHighValueCase = () => [7, 8].includes(Number(this.props.stageId));

    // T&C modal's Reason is part of the credit-stage decision, so it shares
    // the Credit T&C remark's gate. Save must additionally stay usable at
    // stage 11 (post-sanction) since that is the only stage the
    // Post-Sanction remark field itself is even shown/editable at.
    canEditTncReason = () => [7, 8].includes(Number(this.props.stageId));

    canSaveTnc = () =>
        this.canEditTncReason() || Number(this.props.stageId) === 11;

    openCreditAppraisal = () => {
        if (!this.props.caseId) return;
        // Deep-link to the Credit Appraisal tab for Growth Plan / PD /
        // Terms & Condition modals — they live there. The pageID param is the
        // same one already on the URL (case-details); we just swap the tab.
        const path = window.location.pathname.replace(
            /\/([^/]+)$/,
            "/credit_appraisal"
        );
        window.location.assign(path);
    };

    render() {
        const {
            innerRef,
            expanded,
            onToggle,
            caseDetailFields,
            isHighValueCase
        } = this.props;

        const caseCategory = readField(caseDetailFields, "case_category");
        const isDeviationTagged = readField(caseDetailFields, "is_deviation_tagged");
        const deviationCategory = readField(caseDetailFields, "deviation_category");

        return (
            <SectionBlock
                id={SECTION_IDS.ACTIONABLE}
                innerRef={innerRef}
                title={"Actionable"}
                icon={"tags"}
                expanded={expanded}
                onToggle={onToggle}
            >
                <div className={"cc-actionable"}>
                    <div className={"cc-actionable__tile"}>
                        <div className={"cc-actionable__label"}>Case Category</div>
                        <div className={"cc-actionable__value"}>
                            <Select
                                style={{ width: "100%" }}
                                placeholder={"Select Case Category"}
                                value={
                                    caseCategory !== null &&
                                    caseCategory !== undefined
                                        ? Number(caseCategory)
                                        : undefined
                                }
                                loading={this.state.caseCategorySaving}
                                disabled={
                                    this.state.caseCategorySaving ||
                                    !this.canEditCaseCategory()
                                }
                                onChange={this.onChangeCaseCategory}
                            >
                                {CASE_CATEGORY.entrySeq()
                                    .map(([label, value]) => (
                                        <Option key={value} value={value}>
                                            {label}
                                        </Option>
                                    ))
                                    .toArray()}
                            </Select>
                        </div>
                    </div>

                    <div className={"cc-actionable__tile"}>
                        <div className={"cc-actionable__label"}>Residence CPV</div>
                        <div className={"cc-actionable__value"}>
                            <Button
                                size={"small"}
                                onClick={this.openCreditAppraisal}
                            >
                                Manage in Credit Appraisal
                            </Button>
                        </div>
                    </div>

                    <div className={"cc-actionable__tile"}>
                        <div className={"cc-actionable__label"}>Deviation Tag</div>
                        <div className={"cc-actionable__value"}>
                            <Select
                                style={{ width: "100%" }}
                                placeholder={"Select Deviation Tag"}
                                value={
                                    typeof isDeviationTagged === "boolean"
                                        ? isDeviationTagged
                                        : undefined
                                }
                                loading={this.state.deviationSaving}
                                disabled={
                                    this.state.deviationSaving ||
                                    !this.canEditDeviation()
                                }
                                onChange={this.onChangeDeviationTag}
                            >
                                {DEVIATION_TAG_OPTIONS.map(o => (
                                    <Option key={String(o.value)} value={o.value}>
                                        {o.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    {isDeviationTagged === true ? (
                        <div className={"cc-actionable__tile"}>
                            <div className={"cc-actionable__label"}>
                                Deviation Category
                            </div>
                            <div className={"cc-actionable__value"}>
                                <Select
                                    style={{ width: "100%" }}
                                    placeholder={"Select Category"}
                                    value={deviationCategory || undefined}
                                    loading={this.state.deviationSaving}
                                    disabled={
                                        this.state.deviationSaving ||
                                        !this.canEditDeviation()
                                    }
                                    onChange={this.onChangeDeviationCategory}
                                >
                                    {DEVIATION_CATEGORY_OPTIONS.map((val, lbl) => (
                                        <Option key={val} value={val}>
                                            {lbl}
                                        </Option>
                                    ))
                                        .toList()
                                        .toArray()}
                                </Select>
                            </div>
                        </div>
                    ) : null}

                    <div className={"cc-actionable__tile"}>
                        <div className={"cc-actionable__label"}>
                            Terms &amp; Condition
                        </div>
                        <div className={"cc-actionable__value"}>
                            <Button
                                size={"small"}
                                type={"primary"}
                                disabled={!this.canSaveTnc()}
                                onClick={this.openTNCCreditModal}
                            >
                                Terms &amp; Condition
                            </Button>
                        </div>
                    </div>

                    <div className={"cc-actionable__tile cc-actionable__tile--wide"}>
                        <div className={"cc-actionable__label"}>Credit T&C</div>
                        <TextArea
                            rows={2}
                            value={this.getCreditRemark()}
                            placeholder={"Enter Credit T&C remark"}
                            onChange={e =>
                                this.setState({ creditRemarkDraft: e.target.value })
                            }
                            onBlur={this.onBlurCreditRemark}
                            disabled={
                                this.state.creditRemarkSaving ||
                                !this.canEditCreditRemark()
                            }
                        />
                    </div>

                    <div className={"cc-actionable__tile cc-actionable__tile--wide"}>
                        <div className={"cc-actionable__label"}>
                            Post Sanction Remark on T&C
                        </div>
                        <TextArea
                            rows={2}
                            disabled
                            placeholder={"—"}
                            value={
                                (this.getJsonData().tncCredit &&
                                    this.getJsonData().tncCredit
                                        .postSanctionRemark) ||
                                ""
                            }
                        />
                    </div>

                    <div className={"cc-actionable__tile"}>
                        <div className={"cc-actionable__label"}>Growth Plan</div>
                        <div className={"cc-actionable__value"}>
                            <Button
                                size={"small"}
                                disabled={!this.canEditGrowthPlan()}
                                onClick={this.openGrowthPlanModal}
                            >
                                Add / Edit Growth Plan
                            </Button>
                        </div>
                    </div>

                    <div className={"cc-actionable__tile cc-actionable__tile--wide"}>
                        <div className={"cc-actionable__label"}>
                            Growth Plan Summary
                        </div>
                        <TextArea
                            rows={3}
                            disabled
                            placeholder={"No growth plan added yet"}
                            value={
                                (this.props.caseDetail &&
                                    (this.props.caseDetail.get
                                        ? this.props.caseDetail.get(
                                            "growth_plan_parameter"
                                        )
                                        : this.props.caseDetail
                                            .growth_plan_parameter)) ||
                                ""
                            }
                        />
                    </div>

                    <div className={"cc-actionable__tile"}>
                        <div className={"cc-actionable__label"}>
                            Is High Value Case
                        </div>
                        <div className={"cc-actionable__value"}>
                            <Switch
                                checked={Boolean(isHighValueCase)}
                                loading={this.state.highValueSaving}
                                disabled={
                                    this.state.highValueSaving ||
                                    !this.canEditHighValueCase()
                                }
                                onChange={this.onChangeHighValueCase}
                            />
                            {isHighValueCase ? (
                                <Tag color={"green"} style={{ marginLeft: 8 }}>
                                    High Value
                                </Tag>
                            ) : null}
                        </div>
                    </div>
                </div>

                {/* Bundled Limit Assignment — same component + save flow as the
                    Credit Appraisal tab, gated internally by stage/group. */}
                <div className={"cc-actionable__bundled"}>
                    <BundledLimitAssignment
                        caseId={this.props.caseId}
                        onSave={this.updateChildCaseLimit}
                        childLimit={
                            this.props.caseDetail &&
                            this.props.caseDetail.get(
                                "bundled_limit_percentage"
                            )
                        }
                        maxTlLimitUtilisationPerc={
                            this.props.caseDetail &&
                            this.props.caseDetail.get(
                                "max_tl_limit_utilisation_perc"
                            )
                        }
                        stageId={this.props.stageId}
                        userGroup={this.props.userGroup}
                        fetchCaseDetailFields={this.props.fetchCaseDetailFields}
                        caseDetailFields={this.props.caseDetailFields}
                        caseDetailFieldsStatus={
                            this.props.caseDetailFieldsStatus
                        }
                    />
                </div>

                <Modal
                    visible={this.state.growthPlanModalVisible}
                    closable={true}
                    destroyOnClose={true}
                    width={"20%"}
                    footer={null}
                    onCancel={() =>
                        this.setState({ growthPlanModalVisible: false })
                    }
                >
                    <div
                        className={
                            "d-flex flex-column justify-content-center align-items-center"
                        }
                        style={{ width: "100%" }}
                    >
                        <Col span={24}>
                            <label>
                                <b>AUM</b>
                            </label>
                            <Input
                                type={"number"}
                                placeholder={"Enter AUM"}
                                value={this.state.growthPlan.aum}
                                onChange={e =>
                                    this.handleGrowthPlanChange(
                                        "aum",
                                        e.target.value
                                    )
                                }
                            />
                        </Col>
                        <Col span={24} className={"mt-10"}>
                            <label>
                                <b>Number of RTs</b>
                            </label>
                            <Input
                                type={"number"}
                                placeholder={"Enter RT count"}
                                value={this.state.growthPlan.noOfRTs}
                                onChange={e =>
                                    this.handleGrowthPlanChange(
                                        "noOfRTs",
                                        e.target.value
                                    )
                                }
                            />
                        </Col>
                        <Col span={24} className={"mt-10"}>
                            <label>
                                <b>Timeline</b>
                            </label>
                            <DatePicker
                                style={{ width: "100%" }}
                                value={
                                    this.state.growthPlan.timeline
                                        ? moment(this.state.growthPlan.timeline)
                                        : null
                                }
                                disabledDate={d =>
                                    d && d < moment().startOf("day")
                                }
                                onChange={date =>
                                    this.handleGrowthPlanChange("timeline", date)
                                }
                            />
                        </Col>
                        <Row justify={"end"} className={"mt-20"}>
                            <Button
                                type={"primary"}
                                loading={this.state.growthPlanSaving}
                                disabled={
                                    this.state.growthPlanSaving ||
                                    !this.canEditGrowthPlan()
                                }
                                onClick={this.onGrowthPlanSubmit}
                            >
                                Save Growth Plan
                            </Button>
                        </Row>
                    </div>
                </Modal>

                {/* Terms & Condition popup — Reason + Credit T&C + Post-Sanction
                    remark (post-sanction only on stage 11), same as the base
                    Case Details Terms & Condition modal. */}
                <Modal
                    visible={this.state.tncModalVisible}
                    closable
                    destroyOnClose
                    width={"25%"}
                    footer={null}
                    onCancel={() => this.setState({ tncModalVisible: false })}
                >
                    <div className={"d-flex flex-column"} style={{ gap: 16 }}>
                        <label>
                            <b>Reason</b>
                        </label>
                        <Select
                            style={{ width: "100%" }}
                            placeholder={"Select Deviation Tag"}
                            value={this.state.tncCredit.deviationTag}
                            disabled={!this.canEditTncReason()}
                            onChange={value =>
                                this.handleTncChange("deviationTag", value)
                            }
                        >
                            {TNCCredit.map((value, label) => (
                                <Option key={value} value={value}>
                                    {label}
                                </Option>
                            )).toList()}
                        </Select>
                        <label>
                            <b>Credit T&C</b>
                        </label>
                        <TextArea
                            rows={2}
                            placeholder={"Enter Credit T&C remark"}
                            value={this.state.tncCredit.creditRemark}
                            onChange={e =>
                                this.handleTncChange(
                                    "creditRemark",
                                    e.target.value || ""
                                )
                            }
                            disabled={!this.canEditCreditRemark()}
                        />
                        {[11].includes(Number(this.props.stageId)) ? (
                            <div className={"d-flex flex-column"}>
                                <label className={"mb-10"}>
                                    <b>Post Sanction Remark on T&C</b>
                                </label>
                                <TextArea
                                    rows={2}
                                    placeholder={"Enter Post Sanction remark"}
                                    value={
                                        this.state.tncCredit.postSanctionRemark
                                    }
                                    onChange={e =>
                                        this.handleTncChange(
                                            "postSanctionRemark",
                                            e.target.value || ""
                                        )
                                    }
                                />
                            </div>
                        ) : null}
                        <Button
                            type={"primary"}
                            className={"mt-10"}
                            loading={this.state.tncSaving}
                            disabled={
                                this.state.tncSaving || !this.canSaveTnc()
                            }
                            onClick={this.onTNCCreditSubmit}
                        >
                            Save
                        </Button>
                    </div>
                </Modal>

                {/* Deviation Tag "Yes" — force a Deviation Category before the
                    flag is committed. */}
                <Modal
                    title={"Tag Deviation"}
                    visible={this.state.deviationTagModalVisible}
                    okText={"Save"}
                    onOk={this.submitDeviationTagModal}
                    confirmLoading={this.state.deviationTagSubmitting}
                    onCancel={() =>
                        this.setState({
                            deviationTagModalVisible: false,
                            pendingDeviationCategory: null
                        })
                    }
                    destroyOnClose
                    width={400}
                    okButtonProps={{
                        disabled: !this.state.pendingDeviationCategory
                    }}
                >
                    <div className={"d-flex flex-column"} style={{ gap: 8 }}>
                        <label>
                            <b>
                                Deviation Category
                                <span style={{ color: "red" }}> *</span>
                            </b>
                        </label>
                        <Select
                            style={{ width: "100%" }}
                            placeholder={"Select Deviation Category"}
                            value={
                                this.state.pendingDeviationCategory || undefined
                            }
                            onChange={value =>
                                this.setState({
                                    pendingDeviationCategory: value
                                })
                            }
                        >
                            {DEVIATION_CATEGORY_OPTIONS.map((value, label) => (
                                <Option key={value} value={value}>
                                    {label}
                                </Option>
                            )).toList()}
                        </Select>
                    </div>
                </Modal>
            </SectionBlock>
        );
    }
}

ActionableSection.propTypes = {
    innerRef: PropTypes.object,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // Immutable caseDetailFields slice (case-category, is_deviation_tagged, etc.)
    caseDetailFields: PropTypes.object,
    caseDetailFieldsStatus: PropTypes.string,
    // caseData — json_data lives here (T&C, growth plan).
    caseData: PropTypes.object,
    // Immutable case record — bundled_limit_percentage / max_tl_limit_utilisation_perc.
    caseDetail: PropTypes.object,
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // User groups [{ id, name }] — Bundled Limit Assignment edit gate.
    userGroup: PropTypes.array,
    isHighValueCase: PropTypes.bool,
    fetchCaseDetail: PropTypes.func,
    fetchCaseDetailFields: PropTypes.func,
    updateCaseDetail: PropTypes.func
};

export default ActionableSection;
